import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { ArrowLeft, Save, Send, X, Plus, CheckCircle2, FileText, Trash2, Upload, Lock } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TECH_OPTIONS = ["React", "Python", "FastAPI", "Node.js", "Flutter", "MongoDB", "PostgreSQL", "AWS", "GCP", "Firebase", "TensorFlow", "Next.js", "Vue", "Django", "Rust", "Go"];
const CATEGORIES = ["AI", "Sustainability", "Fintech", "EdTech", "Health", "Hardware", "Social Impact", "Other"];
const STAGES = [
  { v: "just_an_idea", l: "Just an idea" },
  { v: "some_research", l: "Some research done" },
  { v: "early_prototype", l: "Early prototype / MVP" },
];
const INNOVATION_TYPES = [
  "Completely new idea (nothing like this exists)",
  "Existing idea, new market or audience",
  "Existing idea, better execution",
  "Combining two existing ideas in a new way",
  "Hardware or physical solution",
  "Social or community impact",
];
const BUDGETS = ["Under ₹10,000", "₹10,000 – ₹50,000", "₹50,000 – ₹2,00,000", "Above ₹2,00,000", "Not sure yet"];

const MAX_DOC_BYTES = 5 * 1024 * 1024;

const EMPTY_FORM = {
  project_title: "", problem_statement: "", proposed_solution: "",
  target_audience: "", category: "",
  innovation_type: "", idea_stage: "", pitch_video_link: "",
  // optional
  tech_stack: [], market_size: "", estimated_budget: "",
  team_skills: "", competitor_awareness: "",
  supporting_doc_url: "", supporting_doc_filename: "", supporting_doc_size: null,
  demo_link: "",
};

export default function SubmitRound1() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [techCustom, setTechCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docMode, setDocMode] = useState("upload"); // "upload" | "link"
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/submissions/r1");
        setState(data);
        if (data.submission) {
          setForm({ ...EMPTY_FORM, ...data.submission });
          // If the saved URL is an external link (not our /api/submissions/doc/* path),
          // switch the toggle to "link" mode so the user sees their pasted URL.
          const u = data.submission.supporting_doc_url;
          if (u && !u.startsWith("/api/submissions/doc/")) setDocMode("link");
        }
      } catch (err) { toast.error(errorMessage(err)); }
      setLoading(false);
    })();
  }, []);

  const isSubmitted = state?.submission?.status === "submitted";
  const readOnly = isSubmitted || !state?.eligible;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTech = (t) => {
    setForm(f => ({ ...f, tech_stack: f.tech_stack.includes(t) ? f.tech_stack.filter(x => x !== t) : [...f.tech_stack, t] }));
  };
  const addCustomTech = () => {
    const v = techCustom.trim();
    if (v && !form.tech_stack.includes(v)) setForm(f => ({ ...f, tech_stack: [...f.tech_stack, v] }));
    setTechCustom("");
  };

  // ----- Required-only validator for final Submit -----
  const validateRequired = () => {
    if (!form.project_title.trim()) return "Project title is required";
    if (!form.problem_statement.trim()) return "Problem statement is required";
    if (!form.proposed_solution.trim()) return "Proposed solution is required";
    if (!form.target_audience.trim()) return "Target audience is required";
    if (!form.category) return "Pick a category";
    if (!form.innovation_type) return "Select innovation type";
    if (!form.idea_stage) return "Pick an idea stage";
    if (!/^https?:\/\/\S+\.\S+/.test(form.pitch_video_link.trim())) return "Pitch video link must be a valid URL";
    return null;
  };

  const cleanPayload = () => {
    // Send empty strings for required, but null for unset optional fields
    const p = { ...form };
    ["market_size", "estimated_budget", "team_skills", "competitor_awareness",
     "supporting_doc_url", "supporting_doc_filename", "demo_link"].forEach(k => {
      if (p[k] === "" || p[k] === undefined) p[k] = null;
    });
    if (!p.supporting_doc_size) p.supporting_doc_size = null;
    return p;
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post("/submissions/r1/save", cleanPayload());
      toast.success("Draft saved");
      const { data } = await api.get("/submissions/r1");
      setState(data);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const finalSubmit = async () => {
    const err = validateRequired();
    if (err) { toast.error(err); setConfirming(false); return; }
    setBusy(true);
    try {
      await api.post("/submissions/r1/submit", cleanPayload());
      toast.success("Submitted! Best of luck.");
      navigate("/team/dashboard");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); setConfirming(false); }
  };

  const uploadPDF = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Only PDF files are allowed"); return; }
    if (file.size > MAX_DOC_BYTES) { toast.error("File too large — max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${REACT_APP_BACKEND_URL}/api/submissions/r1/upload-doc`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Upload failed");
      }
      const json = await res.json();
      setForm(f => ({ ...f, supporting_doc_url: json.url, supporting_doc_filename: json.filename, supporting_doc_size: json.size }));
      toast.success("PDF uploaded");
    } catch (err) { toast.error(err.message || "Upload failed"); } finally { setUploading(false); }
  };

  const removePDF = () => setForm(f => ({ ...f, supporting_doc_url: "", supporting_doc_filename: "", supporting_doc_size: null }));

  if (loading) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>;

  // Only the team leader can submit — non-leaders see a clear message (not the editable form).
  if (state?.team && state?.is_leader === false) {
    return (
      <div className="App" data-testid="submit-r1-not-leader">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16 text-center">
          <button onClick={() => navigate("/team/dashboard")} className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-8 mx-auto"><ArrowLeft className="w-4 h-4" /> Back to dashboard</button>
          <div className="inline-flex items-center gap-2 badge badge-invited mb-6"><Lock className="w-3.5 h-3.5" /> Team leader only</div>
          <h1 className="font-display text-4xl text-white">Only your team leader can submit</h1>
          <p className="text-gray-400 mt-4 leading-relaxed">
            Round 1 for <span className="text-white">{state.team.name}</span> is submitted by your team leader{state.leader_name ? <> (<span className="text-white">{state.leader_name}</span>)</> : ""}. You can track your team's progress from the dashboard.
          </p>
          {isSubmitted && <p className="text-emerald-400 mt-4" data-testid="r1-already-submitted-note">Good news — your team has already submitted Round 1.</p>}
          <button onClick={() => navigate("/team/dashboard")} className="btn-primary mt-8" data-testid="r1-not-leader-dashboard-btn">Go to dashboard</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!state?.eligible && !isSubmitted) {
    return (
      <div className="App" data-testid="submit-r1-not-eligible">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
          <button onClick={() => navigate("/team/dashboard")} className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back to dashboard</button>
          <h1 className="font-display text-4xl text-white">Round 1 submission is <span className="text-brand">not available</span> right now</h1>
          <p className="text-gray-400 mt-4">{state?.reason || "Check back later."}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App" data-testid="submit-r1-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/team/dashboard")} className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back to dashboard</button>
        <div className="label-eyebrow mb-2">Round 1 · Pitch</div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-4xl text-white">Submit your idea</h1>
          {isSubmitted && <span className="badge badge-accepted" data-testid="submitted-badge">submitted</span>}
        </div>
        {isSubmitted && <p className="text-sm text-gray-400 mt-3">This submission is final and cannot be edited.</p>}

        <div className="mt-10 space-y-7">
          {/* 1. Project Title */}
          <Field label="Project Title" testid="r1-title">
            <input disabled={readOnly} value={form.project_title} onChange={e => set("project_title", e.target.value)}
              maxLength={120} className="input-field" placeholder="e.g. CampusSync" />
          </Field>

          {/* 2. Problem Statement */}
          <Field label="Problem Statement" testid="r1-problem"
                 counter={`${form.problem_statement.length}/500`}
                 hint="What problem are you solving and why does it matter?">
            <textarea disabled={readOnly} value={form.problem_statement} onChange={e => set("problem_statement", e.target.value.slice(0, 500))}
              rows={4} maxLength={500} className="input-field resize-none" data-testid="r1-problem-input" />
          </Field>

          {/* 3. Proposed Solution */}
          <Field label="Proposed Solution" testid="r1-solution"
                 counter={`${form.proposed_solution.length}/500`}
                 hint="What are you building, and how does it work?">
            <textarea disabled={readOnly} value={form.proposed_solution} onChange={e => set("proposed_solution", e.target.value.slice(0, 500))}
              rows={4} maxLength={500} className="input-field resize-none" data-testid="r1-solution-input" />
          </Field>

          {/* 4. Target Audience */}
          <Field label="Target Audience" testid="r1-audience">
            <input disabled={readOnly} value={form.target_audience} onChange={e => set("target_audience", e.target.value)}
              maxLength={200} className="input-field" placeholder="College students, small business owners, etc." />
          </Field>

          {/* 5. Category */}
          <Field label="Category" testid="r1-category">
            <select disabled={readOnly} value={form.category} onChange={e => set("category", e.target.value)} className="input-field" data-testid="r1-category-select">
              <option value="">Select a category</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          {/* 6. Innovation Type */}
          <Field label="What best describes your innovation?" testid="r1-innovation-type">
            <select disabled={readOnly} value={form.innovation_type} onChange={e => set("innovation_type", e.target.value)} className="input-field" data-testid="r1-innovation-select">
              <option value="">Select innovation type</option>
              {INNOVATION_TYPES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>

          {/* 7. Idea Stage */}
          <Field label="Idea Stage" testid="r1-stage">
            <div className="space-y-2">
              {STAGES.map(s => (
                <label key={s.v} className={`flex items-center gap-3 border border-white/10 p-3 cursor-pointer ${form.idea_stage === s.v ? "border-brand/60 bg-brand/5" : ""}`}>
                  <input disabled={readOnly} type="radio" name="stage" value={s.v} checked={form.idea_stage === s.v}
                    onChange={e => set("idea_stage", e.target.value)}
                    className="accent-[#FF6B2B]" data-testid={`stage-${s.v}`} />
                  <span className="text-sm text-gray-300">{s.l}</span>
                </label>
              ))}
            </div>
          </Field>

          {/* 8. Pitch Video Link */}
          <Field label="Pitch Video Link" testid="r1-video" hint="Record a 2 to 3 minute video explaining your idea">
            <input disabled={readOnly} value={form.pitch_video_link} onChange={e => set("pitch_video_link", e.target.value)}
              className="input-field" placeholder="YouTube / Loom / Google Drive URL" data-testid="r1-video-input" />
          </Field>

          {/* ===== OPTIONAL FIELDS ===== */}
          <div className="border-t border-white/5 pt-6" />

          {/* 9. Tech Stack */}
          <Field label="Tech Stack" testid="r1-tech" optional
                 hint="Skip this if your idea is hardware-based or tech stack is not yet decided">
            <div className="flex flex-wrap gap-2 mb-3">
              {TECH_OPTIONS.map(t => (
                <button type="button" key={t} disabled={readOnly}
                  onClick={() => toggleTech(t)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${form.tech_stack.includes(t) ? "bg-brand text-black border-brand" : "border-white/15 text-gray-400 hover:text-white"}`}
                  data-testid={`tech-${t}`}>{t}</button>
              ))}
              {form.tech_stack.filter(t => !TECH_OPTIONS.includes(t)).map(t => (
                <span key={t} className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest bg-brand/20 text-brand border border-brand/40 flex items-center gap-1">
                  {t}
                  {!readOnly && <button onClick={() => toggleTech(t)}><X className="w-3 h-3" /></button>}
                </span>
              ))}
            </div>
            {!readOnly && (
              <div className="flex gap-2">
                <input type="text" value={techCustom} onChange={e => setTechCustom(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addCustomTech())}
                  placeholder="Add custom tech…" className="input-field flex-1" />
                <button type="button" onClick={addCustomTech} className="btn-secondary !py-2 !px-3"><Plus className="w-4 h-4" /></button>
              </div>
            )}
          </Field>

          {/* 10. Market Size */}
          <Field label="How large is this problem?" testid="r1-market-size" optional
                 counter={`${(form.market_size || "").length}/200`}>
            <input disabled={readOnly} value={form.market_size || ""} onChange={e => set("market_size", e.target.value.slice(0, 200))}
              maxLength={200} className="input-field"
              placeholder="Ex: 10 million college students in India struggle with career clarity"
              data-testid="r1-market-size-input" />
          </Field>

          {/* 11. Estimated Budget */}
          <Field label="Estimated budget to execute this idea" testid="r1-budget" optional>
            <select disabled={readOnly} value={form.estimated_budget || ""} onChange={e => set("estimated_budget", e.target.value)} className="input-field" data-testid="r1-budget-select">
              <option value="">Select an estimate</option>
              {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>

          {/* 12. Team Skills */}
          <Field label="What skills does your team bring to build this?" testid="r1-team-skills" optional
                 counter={`${(form.team_skills || "").length}/200`}>
            <input disabled={readOnly} value={form.team_skills || ""} onChange={e => set("team_skills", e.target.value.slice(0, 200))}
              maxLength={200} className="input-field"
              placeholder="Ex: We have a frontend dev, a designer, and a business analyst"
              data-testid="r1-team-skills-input" />
          </Field>

          {/* 13. Competitor Awareness */}
          <Field label="Are there existing solutions? How are you different?" testid="r1-competitor" optional
                 counter={`${(form.competitor_awareness || "").length}/300`}>
            <textarea disabled={readOnly} value={form.competitor_awareness || ""} onChange={e => set("competitor_awareness", e.target.value.slice(0, 300))}
              rows={3} maxLength={300} className="input-field resize-none"
              placeholder="Ex: Zomato exists but doesn't serve tier-2 city street vendors"
              data-testid="r1-competitor-input" />
          </Field>

          {/* 14. Supporting Document / Pitch Deck */}
          <Field label="Supporting document or pitch deck" testid="r1-doc" optional
                 hint="1 to 2 pages max. Idea brief or early deck. PDF upload (≤ 5 MB) or a public link.">
            {form.supporting_doc_url ? (
              <div className="flex items-center justify-between gap-3 p-3 border border-brand/30 bg-brand/5" data-testid="r1-doc-attached">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-brand flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{form.supporting_doc_filename || form.supporting_doc_url}</div>
                    <div className="text-xs text-gray-500 font-mono">{form.supporting_doc_size ? `${(form.supporting_doc_size / 1024).toFixed(1)} KB` : "External link"}</div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button type="button" onClick={async () => {
                        const u = form.supporting_doc_url;
                        if (!u.startsWith("/api/")) { window.open(u, "_blank", "noopener,noreferrer"); return; }
                        try {
                          const resp = await api.get(u, { responseType: "blob" });
                          const url = window.URL.createObjectURL(resp.data);
                          window.open(url, "_blank", "noopener,noreferrer");
                          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                        } catch { toast.error("Unable to open document"); }
                      }}
                     className="text-xs text-brand hover:underline" data-testid="r1-doc-view">View</button>
                  {!readOnly && (
                    <button onClick={removePDF} className="text-xs text-gray-400 hover:text-brand flex items-center gap-1" data-testid="r1-doc-remove">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-3" data-testid="r1-doc-mode-toggle">
                  <button type="button" onClick={() => setDocMode("upload")} disabled={readOnly}
                    className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${docMode === "upload" ? "bg-brand text-black border-brand" : "border-white/15 text-gray-400 hover:text-white"}`}
                    data-testid="r1-doc-mode-upload">
                    Upload PDF
                  </button>
                  <button type="button" onClick={() => setDocMode("link")} disabled={readOnly}
                    className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${docMode === "link" ? "bg-brand text-black border-brand" : "border-white/15 text-gray-400 hover:text-white"}`}
                    data-testid="r1-doc-mode-link">
                    Paste a link
                  </button>
                </div>
                {docMode === "upload" ? (
                  <label className={`flex items-center justify-center gap-3 p-5 border-2 border-dashed border-white/15 hover:border-brand/40 cursor-pointer transition-colors ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="r1-doc-dropzone">
                    {uploading ? <span className="spinner" /> : <Upload className="w-4 h-4 text-gray-400" />}
                    <span className="text-sm text-gray-400">{uploading ? "Uploading..." : "Choose PDF (≤ 5 MB)"}</span>
                    <input type="file" accept="application/pdf" disabled={readOnly || uploading} className="hidden"
                      onChange={e => uploadPDF(e.target.files?.[0])} data-testid="r1-doc-input" />
                  </label>
                ) : (
                  <input
                    type="url" disabled={readOnly}
                    placeholder="Google Drive / Notion / public PDF URL"
                    className="input-field"
                    data-testid="r1-doc-link-input"
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      const v = e.currentTarget.value.trim();
                      if (!/^https?:\/\/\S+\.\S+/.test(v)) { toast.error("Enter a valid http(s) URL"); return; }
                      setForm(f => ({ ...f, supporting_doc_url: v, supporting_doc_filename: null, supporting_doc_size: null }));
                      toast.success("Link added");
                    }}
                    onBlur={(e) => {
                      const v = e.currentTarget.value.trim();
                      if (!v) return;
                      if (!/^https?:\/\/\S+\.\S+/.test(v)) return;
                      setForm(f => ({ ...f, supporting_doc_url: v, supporting_doc_filename: null, supporting_doc_size: null }));
                    }}
                  />
                )}
              </>
            )}
          </Field>

          {/* 15. Demo / Mockup Link */}
          <Field label="Demo, mockup, or early prototype link" testid="r1-demo" optional
                 hint="Optional — only if you have something to show already">
            <input disabled={readOnly} value={form.demo_link || ""} onChange={e => set("demo_link", e.target.value)}
              className="input-field" placeholder="Figma / GitHub / Landing page URL"
              data-testid="r1-demo-input" />
          </Field>

          {!readOnly && (
            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/5">
              <button onClick={saveDraft} disabled={busy} className="btn-secondary flex items-center gap-2" data-testid="r1-save-draft">
                {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save Draft
              </button>
              <button onClick={() => setConfirming(true)} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="r1-submit-btn">
                <Send className="w-4 h-4" /> Submit Idea
              </button>
            </div>
          )}
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-5" data-testid="confirm-modal">
          <div className="card-surface max-w-md w-full">
            <h2 className="font-display text-2xl text-white">Submit final?</h2>
            <p className="text-gray-400 mt-3">Once submitted you cannot edit your submission. Are you sure?</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirming(false)} className="btn-secondary flex-1" data-testid="cancel-submit">Cancel</button>
              <button onClick={finalSubmit} disabled={busy} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="confirm-submit">
                {busy ? <span className="spinner" /> : <CheckCircle2 className="w-4 h-4" />} Yes, submit
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const Field = ({ label, children, testid, hint, counter, optional }) => (
  <div data-testid={testid}>
    <div className="flex items-baseline justify-between gap-2 mb-2">
      <label className="label-eyebrow flex items-center gap-2">
        {label}
        {optional && <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-white/10 text-gray-400 rounded-sm" data-testid={`${testid}-optional`}>Optional</span>}
      </label>
      {counter && <span className="font-mono text-[10px] text-gray-500" data-testid={`${testid}-counter`}>{counter}</span>}
    </div>
    {hint && <div className="text-xs text-gray-500 mb-2">{hint}</div>}
    {children}
  </div>
);
